"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowRight } from "lucide-react";


declare const wvcClient: any;

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-pulse">
        <div className="h-12 bg-muted/30 rounded-md w-3/4 mb-6"></div>
        <div className="flex items-center gap-4 mb-8">
          <div className="size-12 bg-muted/30 rounded-lg"></div>
          <div className="h-4 bg-muted/30 rounded w-32"></div>
        </div>
        <div className="h-[400px] bg-muted/30 rounded-xl mb-8"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 rounded w-full"></div>
          <div className="h-4 bg-muted/30 rounded w-full"></div>
          <div className="h-4 bg-muted/30 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h2 className="text-2xl font-default font-semibold text-foreground mb-4">Article Not Found</h2>
        <p className="text-muted-foreground">We apologize, but we couldn't load the mortgage insights you were looking for.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <header className="mb-10">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight mb-6">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-muted-foreground text-sm">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-secondary">
                {post.author.avatar && post.author.avatar["48"] ? (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                ) : null}
                <AvatarFallback className="bg-secondary text-secondary-foreground">
                  <User className="size-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-foreground">{post.author.name}</p>
                <p className="text-xs font-default">Mortgage Specialist</p>
              </div>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-secondary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 rounded-md overflow-hidden border border-border shadow-xl">
          <img 
            src={post.featuredImage} 
            alt={post.title}
            className="w-full h-auto max-h-[600px] object-cover"
            loading="eager"
          />
        </div>
      )}

      <div 
        className="prose prose-invert prose-lg max-w-none text-muted-foreground [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:text-foreground [&>h2]:mt-12 [&>h2]:mb-6 [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:font-default [&>h3]:text-foreground [&>h3]:mt-8 [&>h3]:mb-4 [&>p]:mb-6 [&>p]:leading-relaxed font-sans [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:bg-secondary/5 [&>blockquote]:py-2 [&>img]:rounded-md [&>img]:my-8 [&>img]:shadow-lg"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || '' }}
      />
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="bg-card border-border shadow-md animate-pulse">
            <div className="h-48 bg-muted/30 rounded-t-md"></div>
            <CardHeader>
              <div className="h-6 bg-muted/30 rounded w-3/4 mb-2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-muted/30 rounded w-full mb-2"></div>
              <div className="h-4 bg-muted/30 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="bg-card border-border shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col rounded-md">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block aspect-video overflow-hidden">
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                loading="lazy"
              />
            </Link>
          )}
          <CardHeader className="flex-grow">
            <CardTitle className="text-xl font-default font-semibold text-foreground line-clamp-2">
              <Link to={post.link || "#"} className="hover:text-secondary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-muted-foreground text-sm line-clamp-3 mb-6 font-sans" dangerouslySetInnerHTML={{ __html: post.excerpt || '' }} />
            <Link to={post.link || "#"}>
              <Button variant="outline" className="w-full group border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-all rounded-md">
                Read Full Guide
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const mainPostQuery = new WP_Query(currentWPQueryParams);

  
  const relatedPostsQuery = new WP_Query({ post_type: 'post', per_page: 3 });

  return (
    <section data-section-id="253075" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        
        {}
        <PostsProvider wp_query={mainPostQuery}>
          <MainPostConsumer />
        </PostsProvider>

        <Separator className="my-16 md:my-24 bg-border opacity-50" />

        {}
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-3xl font-bold font-default text-foreground tracking-tight">Expert Mortgage Insights</h2>
            <Link to="/LoanOptionsPage">
              <Button variant="ghost" className="text-secondary hover:text-secondary/80 hover:bg-secondary/10 font-default">
                View All Articles
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>

      </div>
    </section>
  );
}